import React, { PureComponent, Fragment } from 'react';
import styled from 'styled-components';
import { find } from 'lodash';
import {
  FormGroup,
  FormSelect,
  FormHelpText,
  IconClear,
  ButtonIcon,
} from '@spotify-internal/encore-creator-web';
import { SubcategoryList } from '../SubcategoryList';
import { Category as CategoryType, SelectedCategory, Subcategory } from '../../../../types';
import { FormattedMessage, IntlShape, defineMessages } from 'react-intl';

const InputContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledButtonIcon = styled(ButtonIcon)`
  margin-left: 16px;
`;

interface Props {
  categories?: CategoryType[];
  category: SelectedCategory;
  intl: IntlShape;
  error?: boolean;
  setCategory(key: string, category: { name: string; value: string }): void;
  setSubcategory(key: string, subcategory: Subcategory): void;
  removeCategory(key: string): void;
  removeSubcategory(key: string, subcategory: Subcategory): void;
}

export class Category extends PureComponent<Props> {
  componentWillUnmount() {
    const {
      removeCategory,
      category: { categoryKey, value },
    } = this.props;
    /* istanbul ignore else */
    if (!value) removeCategory(categoryKey);
  }

  get hasCategory() {
    return !!this.props.category.value;
  }

  get errorMessage() {
    const { categoryKey } = this.props.category;
    return categoryKey === 'primaryCategory' ? (
      <FormattedMessage
        id="add-podcast.additional-info-category-select"
        defaultMessage="Please select a category."
      />
    ) : (
      <FormattedMessage
        id="add-podcast.additional-info-category-already-selected"
        defaultMessage="That category has already been selected."
      />
    );
  }

  handleOnChange = ({ target: { value } }) => {
    const { name } = find(this.props.categories, { value }) as CategoryType;
    const {
      setCategory,
      category: { categoryKey },
    } = this.props;
    setCategory(categoryKey, { name, value });
  };

  render() {
    if (!this.props.categories) return null;

    const {
      categories,
      category: { name, value, subcategories, categoryKey },
      error,
      removeCategory,
    } = this.props;
    const label =
      categoryKey === 'primaryCategory' ? (
        <FormattedMessage
          id="add-podcast.additional-info-category-primary"
          defaultMessage="Primary category"
        />
      ) : (
        <FormattedMessage
          id="add-podcast.additional-info-category-additional"
          defaultMessage="Additional category"
        />
      );
    const subcategoryOptions = this.hasCategory
      ? (find(categories, { value }) as CategoryType).subcategories
      : [];

    const categoryMessages = defineMessages({
      selectCategory: {
        id: 'add-podcast.additional-info-select-category',
        defaultMessage: 'Select category',
      },
    });

    return (
      <Fragment>
        <FormGroup label={label} labelFor={categoryKey} indicator="required">
          <InputContainer>
            <FormSelect
              value={value}
              id={categoryKey}
              onChange={this.handleOnChange}
              error={error}
              data-testid="category-select"
            >
              <option value="" disabled>
                {this.props.intl.formatMessage(categoryMessages.selectCategory)}
              </option>
              {categories.map(c => (
                <option key={c.value} value={c.value}>
                  {c.name}
                </option>
              ))}
            </FormSelect>
            {categoryKey !== 'primaryCategory' && (
              <StyledButtonIcon
                onClick={() => removeCategory(categoryKey)}
                data-qa="remove-category-button"
                data-testid="remove-category-button"
              >
                <IconClear aria-label="IconClear" iconSize={24} />
              </StyledButtonIcon>
            )}
          </InputContainer>
          {error && <FormHelpText error>{this.errorMessage}</FormHelpText>}
        </FormGroup>
        {/* istanbul ignore next */ subcategoryOptions.length > 0 && (
          <FormGroup
            label={
              <FormattedMessage
                id="add-podcast.additional-info-category-already-selected"
                defaultMessage="Choose up to 3 sub-categories of {name}"
                values={{
                  name: name,
                }}
              />
            }
          >
            <SubcategoryList
              selectedSubcategories={subcategories}
              subcategories={subcategoryOptions}
              categoryKey={categoryKey}
              setSubcategory={this.props.setSubcategory}
              removeSubcategory={this.props.removeSubcategory}
            />
          </FormGroup>
        )}
      </Fragment>
    );
  }
}
